import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight, Loader2 } from "lucide-react";
import { useAnalyst } from "@/context/AnalystContext";
import api from "@/lib/api";

export default function AIBriefing() {
  const { ask } = useAnalyst() || {};
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/ai/briefing")
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      data-testid="ai-briefing"
      className="relative rounded-2xl border border-border bg-surface overflow-hidden"
    >
      <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-accent/10 blur-3xl pointer-events-none" />
      <div className="relative p-8 flex flex-col lg:flex-row gap-8">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 font-mono-data text-[10px] uppercase tracking-[0.25em] text-accent mb-4">
            <Sparkles className="w-3.5 h-3.5" /> AI Daily Market Briefing
          </div>
          {loading ? (
            <div className="flex items-center gap-2 font-mono-data text-sm text-muted-foreground animate-pulse py-6" data-testid="ai-briefing-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Generating today's briefing…
            </div>
          ) : (
            <>
              <h3 className="font-display text-2xl sm:text-3xl tracking-tight font-medium leading-snug" data-testid="ai-briefing-headline">
                {data?.headline || "Today's verified luxury market"}
              </h3>
              <p className="text-muted-foreground text-base mt-4 leading-relaxed max-w-2xl" data-testid="ai-briefing-summary">
                {data?.summary}
              </p>
              {data?.highlights?.length > 0 && (
                <ul className="mt-5 space-y-2.5">
                  {data.highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-accent shrink-0" />
                      <span className="leading-relaxed">{h}</span>
                    </li>
                  ))}
                </ul>
              )}
              <button
                onClick={() => ask?.("Give me a deeper analysis of today's luxury market and where the best value is right now.")}
                data-testid="ai-briefing-deepdive"
                className="mt-6 inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/5 px-4 py-2 text-sm text-accent hover:bg-accent/10 transition-colors"
              >
                <Sparkles className="w-4 h-4" /> Ask the AI to go deeper <ArrowRight className="w-4 h-4" />
              </button>
            </>
          )}
        </div>

        {!loading && data?.metrics?.length > 0 && (
          <div className="lg:w-56 shrink-0 grid grid-cols-2 lg:grid-cols-1 gap-3" data-testid="ai-briefing-metrics">
            {data.metrics.map((m) => (
              <div key={m.label} className="rounded-xl border border-border bg-background/40 px-4 py-3">
                <div className="font-display text-2xl tracking-tight">{m.value}</div>
                <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{m.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}
